// src/pages/UserAdmin.tsx
import React, { useEffect, useState } from "react";
import { collection, getDocs, getDoc, doc } from "firebase/firestore"; // <-- Denna rad är korrekt
import AiPerformanceDisplay from "../components/AiPerformanceDisplay"; // <-- Här är ändringen
import { httpsCallable, getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { db } from "../firebase";


/**
 * Bindningar till Cloud Functions
 * (namnen här ska matcha dina deployade CF-funktionsnamn)
 */
const functions = getFunctions(undefined, "europe-west1");

// [NYTT] Anslut till emulatorn automatiskt i dev
// if (location.hostname === "localhost" || location.hostname === "127.0.0.1") {
   // try {
     //   connectFunctionsEmulator(functions, "localhost", 5001);
       // console.log("[UserAdmin] Using Functions emulator at localhost:5001");
    // } catch {
        // ignorera om redan ansluten
    // }
// }
const fnListUsers = httpsCallable<any, any>(functions, "listUsers");
const fnDeleteUser = httpsCallable<any, any>(functions, "deleteUser");
const fnTriggerReset = httpsCallable<any, any>(functions, "triggerPasswordReset");
const setUserClaims = httpsCallable<any, any>(functions, "setUserClaims");
// överst bland dina state:


// === [NYTT] Klient-anrop till Cloud Function: deleteUserAccount ===
type DeleteUserAccountRequest = { uid: string };
type DeleteUserAccountResponse = {
    ok: true;
    deleted: { auth: boolean; userDoc: boolean };
    auditId?: string;
};

async function gwDeleteUserAccount(uid: string): Promise<DeleteUserAccountResponse> {
    const fns = getFunctions(undefined, "europe-west1");
    const call = httpsCallable<DeleteUserAccountRequest, DeleteUserAccountResponse>(fns, "deleteUserAccount");
    const res = await call({ uid });
    return res.data;
}

export default function UsersAdmin() {
    type AdminUserRow = {
        uid: string;
        email?: string;
        displayName?: string;
        disabled: boolean;
        role: "admin" | "user" | "customer" | "unassigned";
        emailVerified: boolean;
        createdAt?: string;
        lastSignInTime?: string;
    };

    type CustomerOpt = { key: string; name: string };

    const [rows, setRows] = useState<AdminUserRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);

    const [customers, setCustomers] = useState<CustomerOpt[]>([]);
    const [saving, setSaving] = useState(false);
    const [aiKey, setAiKey] = useState(0);

    // Redigeringspanelens state
    const [edit, setEdit] = useState<{
        uid: string;
        email?: string;
        role: "admin" | "user" | "customer" | "unassigned";
        status: "pending" | "active" | "disabled";
        customerKeys: string[];
    } | null>(null);

    // ---- Ladda användare + kunder ----
    const load = async () => {
        setLoading(true);
        try {
            const res: any = await fnListUsers({});
            setRows((res?.data?.users as AdminUserRow[]) || []);
            setMsg(null);
        } catch (e: any) {
            console.error("[UserAdmin] load failed", e);
            setMsg(e?.message || "Kunde inte hämta användare.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void load();
        void loadCustomers();
    }, []);

    async function loadCustomers() {
        try {
            const snap = await getDocs(collection(db, "customers"));
            const opts: { key: string; name: string }[] = snap.docs
                .map((d) => {
                    const data = d.data() as any;
                    const name = String(data?.name ?? d.id);
                    return { key: d.id, name };   // ✅ använd doc.id som key (stabil ID)
                })
                .sort((a, b) => a.name.localeCompare(b.name, "sv"));
            setCustomers(opts);
        } catch (e) {
            console.warn("Kunde inte ladda customers:", e);
        }
    }


    // Alias så att din befintliga kod som refererar till customerListOpts fungerar rakt av.
    const customerListOpts = customers;

    // --- Helper: mappa ev. namn → IDs med hjälp av customerListOpts
    function toIdKeys(keys: unknown, opts: Array<{ key: string; name: string }>): string[] {
        const src = Array.isArray(keys) ? keys.map(String) : [];
        const byName = new Map(opts.map(o => [o.name.toLowerCase(), o.key]));
        const idSet = new Set(opts.map(o => o.key));
        return src.map(k => {
            const kk = k.trim();
            if (idSet.has(kk)) return kk;                         // redan ett ID
            const asId = byName.get(kk.toLowerCase());            // matcha på namn → id
            return asId ?? kk;                                     // fallback: lämna orört
        });
    }

    // ---- Åtgärder ----
    const doDelete = async (uid: string) => {
        if (!confirm("⚠️ Vill du verkligen radera användaren permanent?")) return;

        setMsg("Raderar användare...");
        try {
            const res = await gwDeleteUserAccount(uid);
            const parts: string[] = [];
            if (res.deleted.auth) parts.push("Auth");
            if (res.deleted.userDoc) parts.push("Firestore");
            setMsg(`✅ Raderad (${parts.join(" + ")})`);
            await load();
        } catch (e: any) {
            console.error("[UserAdmin] deleteUserAccount failed:", e);
            alert("Kunde inte radera användare: " + (e?.message || String(e)));
        } finally {
            setMsg(null);
        }
    };


    const sendReset = async (email: string) => {
        const res: any = await fnTriggerReset({ email });
        const link = res.data.resetLink as string;
        setMsg(`Reset-länk genererad: ${link}`);
    };

    // Öppna redigeringspanel; läs users/<uid> och förkryssa nuvarande kunder (alltid som IDs)
    async function openEdit(u: AdminUserRow) {
        let role: "admin" | "user" | "customer" | "unassigned" = (u.role as any) ?? "unassigned";
        let status: "pending" | "active" | "disabled" = (u as any).status ?? "pending";

        // 1) Hämta rå-keys från users/<uid> (kan vara id/ids/customers/customerKeys)
        let rawKeys: string[] = [];
        try {
            const snap = await getDoc(doc(db, "users", u.uid));
            if (snap.exists()) {
                const d = snap.data() as any;
                role = d.role ?? role;
                status = d.status ?? status;

                if (Array.isArray(d.customerKeys)) rawKeys = d.customerKeys.map(String);
                else if (Array.isArray(d.customerIds)) rawKeys = d.customerIds.map(String);
                else if (d.customers && typeof d.customers === "object") {
                    rawKeys = Object.keys(d.customers).filter((k) => !!d.customers[k]).map(String);
                }
            }
        } catch {
            /* ignorera läsfel – vi faller tillbaka nedan */
        }

        // 2) Fallback från tabellraden om users/<uid> saknar info
        if (rawKeys.length === 0) {
            const row: any = u;
            if (Array.isArray(row.customerKeys)) rawKeys = row.customerKeys.map(String);
            else if (Array.isArray(row.customerIds)) rawKeys = row.customerIds.map(String);
            else if (row.customers && typeof row.customers === "object") {
                rawKeys = Object.keys(row.customers).filter((k) => !!row.customers[k]).map(String);
            }
        }

        // 3) Normalisera till customers/{id} med hjälp av customerListOpts (ID + name)
        const toIdKeys = (keys: string[], opts: Array<{ key: string; name: string }>): string[] => {
            const byName = new Map(opts.map(o => [o.name.toLowerCase(), o.key]));
            const idSet = new Set(opts.map(o => o.key));
            return Array.from(new Set(keys.map(k => {
                const kk = String(k).trim();
                if (idSet.has(kk)) return kk;                          // redan ett ID
                const asId = byName.get(kk.toLowerCase());             // namn → id
                return asId ?? kk;                                      // fallback: lämna orört
            })));
        };

        const idKeys = toIdKeys(rawKeys, customerListOpts);

        // 4) Sätt edit-state (alltid IDs i customerKeys)
        setEdit({
            uid: u.uid,
            email: u.email || "",
            role,
            status,
            customerKeys: idKeys, // ✅ IDs
        } as any);
    }

    // --- Toggle-funktion: håll alltid IDs i edit.customerKeys ---
    const toggleCustomerKey = (id: string) => {
        setEdit((p) => {
            if (!p) return p;
            const has = Array.isArray(p.customerKeys) && p.customerKeys.includes(id);
            return {
                ...p,
                customerKeys: has
                    ? p.customerKeys.filter((k: string) => k !== id)
                    : [...(p.customerKeys || []), id],
            };
        });
    };

    async function onSaveClaims() {
        if (!edit) return;
        if (edit.role === "customer" && (!Array.isArray(edit.customerKeys) || edit.customerKeys.length === 0)) {
            alert("Välj minst en kund för kundroll.");
            return;
        }
        setSaving(true);
        try {
            // Normalisera till IDs med hjälp av customerListOpts
            const byName = new Map(customerListOpts.map(o => [o.name.toLowerCase(), o.key]));
            const idSet = new Set(customerListOpts.map(o => o.key));
            const idKeys = (Array.isArray(edit.customerKeys) ? edit.customerKeys : [])
                .map(String)
                .map(k => {
                    const kk = k.trim();
                    if (idSet.has(kk)) return kk;                 // redan ID
                    const asId = byName.get(kk.toLowerCase());    // namn → id
                    return asId ?? kk;                             // fallback
                });

            await setUserClaims({
                uid: edit.uid,
                role: edit.role,
                // send status only when it matters
                status: edit.role === "customer" ? edit.status : (edit.role === "unassigned" ? "pending" : edit.status),
                customerKeys: edit.role === "customer" ? idKeys : undefined,
            });

            setEdit(null);
            await load();
            alert("Behörighet uppdaterad. Be användaren logga in igen.");
        } catch (e: any) {
            console.error(e);
            alert("Kunde inte spara: " + (e?.message || String(e)));
        } finally {
            setSaving(false);
        }
    }

    function StatusBadge({ status }: { status?: "pending" | "active" | "disabled" }) {
        const s = status ?? "pending";
        let classes = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ";

        if (s === "active") classes += "bg-emerald-50 text-emerald-700 border-emerald-200";
        else if (s === "pending") classes += "bg-amber-50 text-amber-700 border-amber-200";
        else if (s === "disabled") classes += "bg-gray-50 text-gray-600 border-gray-200";
        else classes += "bg-gray-100 text-gray-800"; // Fallback

        return <span className={classes}>{s}</span>;
    }

    return (
        <div className="p-6 max-w-[1600px] mx-auto">
            <header className="mb-6">
                <h1 className="gw-h1">Användare</h1>
            </header>

            <div className="flex justify-end mb-4">
                <button
                    type="button"
                    onClick={() => load()}
                    disabled={loading}
                    className="gw-btn gw-btn-secondary"
                >
                    {loading ? "Laddar..." : "Uppdatera listan"}
                </button>
            </div>


            {msg && (
                <div className="mb-6 p-4 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
                    {msg}
                </div>
            )}

            {loading ? (
                <div className="text-muted italic">Laddar användare...</div>
            ) : (
                <div className="gw-table-wrap">
                    <table>
                        <thead>
                            <tr>
                                <th>Namn</th>
                                <th>E-post</th>
                                <th>Verifierad</th>
                                <th>Status</th>
                                <th>Roll</th>
                                <th className="min-w-[200px]">Senast inloggad</th>
                                <th className="text-right min-w-[260px]">Åtgärder</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((u) => (
                                <tr key={u.uid}>
                                    <td className="font-medium">{u.displayName || "-"}</td>
                                    <td>{u.email}</td>
                                    <td>
                                        {u.emailVerified ? (
                                            <span className="text-emerald-600 font-medium">Ja</span>
                                        ) : (
                                            <span className="text-muted">Nej</span>
                                        )}
                                    </td>
                                    <td>
                                        <StatusBadge status={(u as any).status as any} />
                                    </td>
                                    <td>
                                        <span className="inline-block px-2 py-1 rounded bg-surface-2 text-xs font-mono text-muted border border-border">
                                            {u.role}
                                        </span>
                                    </td>
                                    <td className="text-sm text-muted">{u.lastSignInTime || "-"}</td>
                                    <td className="text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                type="button"
                                                className="gw-btn gw-btn-secondary gw-btn-sm"
                                                onClick={() => sendReset(u.email!)}
                                                title="Skicka reset-länk"
                                            >
                                                Reset
                                            </button>

                                            <button
                                                type="button"
                                                className="gw-btn gw-btn-secondary gw-btn-sm"
                                                onClick={() => openEdit(u)}
                                            >
                                                Ändra
                                            </button>

                                            <button
                                                type="button"
                                                className="gw-btn gw-btn-danger gw-btn-sm"
                                                onClick={() => doDelete(u.uid)}
                                                title="Radera användare"
                                            >
                                                Radera
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="text-center py-8 text-muted">
                                        Inga användare hittades.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}



            {/* Redigeringspanel */}
            {edit && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4 overflow-y-auto">
                    <div className="gw-card w-full max-w-2xl shadow-xl animate-in fade-in zoom-in-95 duration-200">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-6 pb-4 border-b border-border">
                            <div>
                                <div className="gw-h3 mb-1">Redigera behörighet</div>
                                <div className="text-sm text-muted font-mono">{edit.email || edit.uid}</div>
                            </div>
                            <button
                                onClick={() => setEdit(null)}
                                className="text-muted hover:text-text p-1"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Roll & Status */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-text mb-1.5">Roll</label>
                                <select
                                    className="gw-input"
                                    value={edit.role}
                                    onChange={(e) =>
                                        setEdit((p) => (p ? { ...p, role: e.target.value as "admin" | "user" | "customer" } : p))
                                    }
                                >
                                    <option value="unassigned">unassigned (ingen åtkomst)</option>
                                    <option value="admin">admin</option>
                                    <option value="user">user</option>
                                    <option value="customer">customer</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text mb-1.5">Status</label>
                                <select
                                    className="gw-input"
                                    value={edit.status ?? "pending"}
                                    onChange={(e) =>
                                        setEdit((p) => (p ? { ...p, status: e.target.value as "pending" | "active" | "disabled" } : p))
                                    }
                                >
                                    <option value="pending">pending</option>
                                    <option value="active">active</option>
                                    <option value="disabled">disabled</option>
                                </select>
                            </div>
                        </div>

                        {/* Kunder (visas bara om roll = customer) */}
                        {edit?.role === "customer" && (
                            <div className="mb-6 p-4 bg-surface-2 rounded-md border border-border">
                                <div className="text-sm font-medium text-text mb-3">Kunder (välj en eller flera)</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                                    {customerListOpts.map((opt) => {
                                        const id = String(opt.key);
                                        const name = String(opt.name);
                                        const checked = Array.isArray(edit?.customerKeys) && edit!.customerKeys.includes(id);

                                        return (
                                            <label key={id} className="flex items-center gap-2 p-2 hover:bg-surface rounded cursor-pointer transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleCustomerKey(id)}
                                                    className="rounded border-gray-300 text-primary focus:ring-primary"
                                                />
                                                <span className="text-sm">
                                                    {name} <span className="text-muted text-xs ml-1">({id})</span>
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between pt-4 border-t border-border mt-6">
                            <div className="text-xs text-muted max-w-xs">
                                Sparandet uppdaterar claims + spegeln i users-collection.
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    className="gw-btn gw-btn-secondary"
                                    onClick={() => setEdit(null)}
                                    disabled={saving}
                                >
                                    Avbryt
                                </button>
                                <button
                                    type="button"
                                    className="gw-btn gw-btn-primary"
                                    onClick={onSaveClaims}
                                    disabled={saving}
                                >
                                    {saving ? "Sparar…" : "Spara behörighet"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export { };
